import {
  getRemoteStorageTemplate,
  protocolDefaults,
  REMOTE_STORAGE_PROTOCOLS,
  type RemoteStorageCredentialSummary,
  type RemoteStorageHostKeyResponse,
  type RemoteStorageProtocol,
  type RemoteStorageSecretName,
  type RemoteStorageSecrets,
  usedSecretNames,
} from "common";
import { type JSX, useMemo, useState } from "react";
import {
  remoteStorageErrorMessage,
  useDiscoverHostKey,
  useSaveRemoteStorageCredential,
} from "./remote-storage.api.tsx";

interface RemoteServerFormProps {
  existing?: RemoteStorageCredentialSummary;
  onSaved: (saved: RemoteStorageCredentialSummary) => void;
  onCancel: () => void;
}

const inputClass =
  "mt-1 block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm";
const labelClass = "block text-sm font-medium leading-6 text-gray-900";

const initialVariables = (
  protocol: RemoteStorageProtocol,
  keep: Record<string, string | number> = {}
): Record<string, string | number> => {
  const defaults = protocolDefaults(protocol);
  const values: Record<string, string | number> = {};
  for (const field of getRemoteStorageTemplate(defaults.backend).variables) {
    if (field.fromProtocol) continue;
    if ("port" === field.name) values.port = defaults.port;
    // `||`, not `??`: an empty user name carried over from SFTP must still fall back to FTP's "anonymous".
    else values[field.name] = keep[field.name] || field.defaultValue || "";
  }
  return values;
};

/** The Remote server form, generated from the template of the chosen protocol (data-1-2). */
export function RemoteServerForm({
  existing,
  onSaved,
  onCancel,
}: RemoteServerFormProps): JSX.Element {
  const [protocol, setProtocol] = useState<RemoteStorageProtocol>(
    existing?.protocol ?? "sftp"
  );
  const [name, setName] = useState(existing?.name ?? "");
  const [variables, setVariables] = useState<Record<string, string | number>>(
    existing ? { ...existing.variables } : initialVariables("sftp")
  );
  const [secrets, setSecrets] = useState<RemoteStorageSecrets>({});
  const [hostKey, setHostKey] = useState<RemoteStorageHostKeyResponse | null>(
    null
  );
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const discover = useDiscoverHostKey();
  const save = useSaveRemoteStorageCredential();

  const backend = protocolDefaults(protocol).backend;
  const template = getRemoteStorageTemplate(backend);
  const visibleSecrets = usedSecretNames(backend, variables);
  const moved =
    !existing ||
    existing.variables.host !== variables.host ||
    Number(existing.variables.port) !== Number(variables.port);
  const keyReady =
    !template.pinsHostKey ||
    (!moved && !hostKey) ||
    (null !== hostKey && confirmed);

  const changeProtocol = (next: RemoteStorageProtocol) => {
    setProtocol(next);
    setVariables(
      initialVariables(next, {
        host: variables.host ?? "",
        user: variables.user ?? "",
      })
    );
    setSecrets({});
    setHostKey(null);
    setConfirmed(false);
  };

  const changeVariable = (fieldName: string, value: string) => {
    setVariables(current => ({
      ...current,
      [fieldName]: "port" === fieldName ? Number(value) : value,
    }));
    if ("host" === fieldName || "port" === fieldName) {
      setHostKey(null);
      setConfirmed(false);
    }
  };

  const checkServer = async () => {
    setError(null);
    try {
      setHostKey(
        await discover.mutateAsync({
          host: String(variables.host).trim(),
          port: Number(variables.port),
        })
      );
      setConfirmed(false);
    } catch (failure) {
      setError(remoteStorageErrorMessage(failure));
    }
  };

  const submit = async () => {
    setError(null);
    // A blank secret is left out so the server keeps the stored one.
    const sent: RemoteStorageSecrets = {};
    for (const secretName of visibleSecrets) {
      if (secrets[secretName]) sent[secretName] = secrets[secretName];
    }
    try {
      onSaved(
        await save.mutateAsync({
          id: existing?.id,
          body: {
            name: name.trim(),
            protocol,
            variables,
            secrets: sent,
            hostKey: hostKey?.hostKey,
          },
        })
      );
    } catch (failure) {
      setError(remoteStorageErrorMessage(failure));
    }
  };

  const secretLabel = useMemo(
    () =>
      Object.fromEntries(template.secrets.map(field => [field.name, field])),
    [template]
  );

  return (
    <form
      className="space-y-4"
      onSubmit={event => {
        event.preventDefault();
        void submit();
      }}>
      <div>
        <label className={labelClass} htmlFor="rs-name">
          Name
        </label>
        <input
          id="rs-name"
          className={inputClass}
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>
      <div>
        <label className={labelClass} htmlFor="rs-protocol">
          Protocol
        </label>
        <select
          id="rs-protocol"
          className={inputClass}
          value={protocol}
          disabled={Boolean(existing)}
          onChange={e =>
            changeProtocol(e.target.value as RemoteStorageProtocol)
          }>
          {REMOTE_STORAGE_PROTOCOLS.filter(
            p =>
              !existing ||
              protocolDefaults(p.value).backend === existing.backend
          ).map(p => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      {template.variables
        .filter(field => !field.fromProtocol)
        .map(field => (
          <div key={field.name}>
            <label className={labelClass} htmlFor={`rs-${field.name}`}>
              {field.label}
            </label>
            {"select" === field.kind ? (
              <select
                id={`rs-${field.name}`}
                className={inputClass}
                value={String(variables[field.name] ?? "")}
                onChange={e => changeVariable(field.name, e.target.value)}>
                {field.options!.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`rs-${field.name}`}
                className={inputClass}
                type={"port" === field.kind ? "number" : "text"}
                value={variables[field.name] ?? ""}
                onChange={e => changeVariable(field.name, e.target.value)}
              />
            )}
            {field.help && (
              <p className="mt-1 text-xs text-gray-500">{field.help}</p>
            )}
          </div>
        ))}
      {visibleSecrets.map((secretName: RemoteStorageSecretName) => {
        const field = secretLabel[secretName];
        const saved = Boolean(existing?.secretsSet[secretName]);
        const props = {
          id: `rs-secret-${secretName}`,
          className: inputClass,
          value: secrets[secretName] ?? "",
          placeholder: saved ? "Saved. Leave blank to keep it." : "",
          autoComplete: "off",
          onChange: (e: { target: { value: string } }) =>
            setSecrets(current => ({
              ...current,
              [secretName]: e.target.value,
            })),
        };
        return (
          <div key={secretName}>
            <label className={labelClass} htmlFor={props.id}>
              {field.label}
            </label>
            {field.multiline ? (
              <textarea rows={5} spellCheck={false} {...props} />
            ) : (
              <input type="password" {...props} />
            )}
          </div>
        );
      })}
      {template.pinsHostKey && (
        <div
          className="space-y-2 rounded-md border border-gray-200 p-3"
          data-testid="hostKeyPanel">
          {existing?.hostKeyFingerprint && !hostKey && !moved && (
            <p className="text-sm text-gray-700">
              Confirmed host key: <code>{existing.hostKeyFingerprint}</code>
            </p>
          )}
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            disabled={!variables.host || discover.isPending}
            onClick={() => void checkServer()}>
            Check server
          </button>
          {hostKey && (
            <div className="space-y-1">
              <p className="text-sm text-gray-700">
                Host key fingerprint ({hostKey.keyType}):
              </p>
              <code
                className="block break-all text-sm"
                data-testid="hostKeyFingerprint">
                {hostKey.fingerprint}
              </code>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                />
                This matches the fingerprint the server&apos;s administrator
                gave me
              </label>
            </div>
          )}
        </div>
      )}
      {error && (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!name.trim() || !keyReady || save.isPending}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          Save
        </button>
      </div>
    </form>
  );
}
